/**
 * Presentación de la comparación entre releases.
 *
 * Sin JSX para poder probarlo con el ejecutor de Node, y porque decidir cómo
 * se lee un veredicto es una regla, no una vista.
 *
 * La decisión de fondo: «igual» y «sin datos» tienen que verse distinto. Los
 * dos dejan la fila sin flecha, y si además se escribieran igual el usuario
 * leería «no cambió nada» donde la verdad es «no alcanza para saberlo». Esa
 * confusión es la que hace que alguien apruebe un despliegue creyendo que lo
 * midió.
 */

export type Veredicto = 'peor' | 'mejor' | 'igual' | 'sin_datos';

export interface Presentacion {
  simbolo: string;
  etiqueta: string;
  /** Clase CSS: colorea la fila y la celda. */
  clase: string;
}

const PRESENTACIONES: Record<Veredicto, Presentacion> = {
  peor: { simbolo: '▲', etiqueta: 'Peor', clase: 'peor' },
  mejor: { simbolo: '▼', etiqueta: 'Mejor', clase: 'mejor' },
  igual: { simbolo: '=', etiqueta: 'Sin cambio', clase: 'igual' },
  sin_datos: { simbolo: '·', etiqueta: 'Sin datos suficientes', clase: 'sin-datos' },
};

export function presentar(veredicto: Veredicto): Presentacion {
  return PRESENTACIONES[veredicto] ?? PRESENTACIONES.sin_datos;
}

/**
 * El delta con su signo, en la misma unidad en que se muestran los valores.
 *
 * Dos reglas, y las dos costaron una pasada. La unidad: la celda de latencia
 * decía «1,32 s → 3,35 s» arriba y «+2029» abajo, y una columna con dos
 * unidades obliga a convertir de cabeza justo cuando uno está comparando
 * filas. El signo: es el del cambio, no el del juicio. Bajar la tasa de error
 * es bueno y aun así el delta va negativo — ponerle el signo según si mejoró
 * haría que dos columnas contiguas usaran convenciones opuestas.
 */
export function deltaFormateado(valor: number, formato: (v: number) => string): string {
  if (Math.abs(valor) < 0.05) return '0';
  return `${valor > 0 ? '+' : '−'}${formato(Math.abs(valor))}`;
}

/** Puntos porcentuales, no por ciento: la diferencia entre dos tasas. */
export function puntos(valor: number): string {
  return `${Number(valor.toFixed(1))} pts`;
}

export function milisegundos(valor: number): string {
  return valor >= 1000 ? `${(valor / 1000).toFixed(2)} s` : `${Math.round(valor)} ms`;
}

/**
 * Resumen de una línea para el encabezado.
 *
 * Se cuenta lo que hay, sin redondear a un titular: «2 peor, 1 mejor» dice más
 * que «el release está mal», y deja que quien decide mire las dos cosas.
 */
export function resumirFilas(veredictos: Veredicto[]): string {
  const cuenta = (v: Veredicto) => veredictos.filter((x) => x === v).length;
  const partes: string[] = [];
  if (cuenta('peor')) partes.push(`${cuenta('peor')} peor`);
  if (cuenta('mejor')) partes.push(`${cuenta('mejor')} mejor`);
  if (cuenta('igual')) partes.push(`${cuenta('igual')} sin cambio`);
  if (cuenta('sin_datos')) partes.push(`${cuenta('sin_datos')} sin datos suficientes`);
  return partes.join(' · ') || 'sin procesos en la ventana';
}

/**
 * Pareja sugerida al abrir la pestaña: las dos versiones más recientes.
 *
 * Es la comparación que alguien viene a hacer nueve de cada diez veces, y
 * dejar los selectores vacíos obligaría a elegir dos cosas antes de ver nada.
 */
export function parPorDefecto(versiones: string[]): { a: string; b: string } | null {
  if (versiones.length < 2) return null;
  // La lista llega de la más reciente a la más vieja: la nueva es la primera.
  return { a: versiones[1] as string, b: versiones[0] as string };
}
